import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Box, Typography } from '@mui/material';

import useRouter from '../../utils/useRouter';
import useScreenSize from '../../utils/useScreenSize';
import { CollectionProperties } from '../../types/AppContent';
import { routes } from '../../constants';
import { MenuItemProps } from '../cards/MuiCard/CustomMuiCard/CustomMuiCardActions/CustomMuiCardActions';
import { boxShadows, palette, spaces, typography } from '../../assets/styles/themes/tokens';
import CustomMuiCard from '../cards/MuiCard/CustomMuiCard/CustomMuiCard';
import CollectionContent from '../MyCollections/MyCollectionsList/CollectionContent/CollectionContent';
import SubjectBanners from '../../assets/subjects/banners';
import { unfollowCollection, followCollection } from '../../actions/collections.actions';
import { NotificationContext } from '../../state/NotificationContextProvider';
import useResponsivePadding from '../../utils/useResponsivePadding';

interface CuratedCollectionsListProps {
  collections: CollectionProperties[];
}

const CuratedCollectionsList = ({ collections }: CuratedCollectionsListProps) => {
  const { t } = useTranslation();
  const { router } = useRouter();
  const dispatch = useDispatch();
  const { isXsScreen, isSmScreen, isMdScreen } = useScreenSize();
  const { showNotification } = useContext(NotificationContext);
  const cardPadding = useResponsivePadding();

  const getGridTemplateColumns = () => {
    if (isXsScreen) return 1;
    if (isSmScreen || isMdScreen) return 2;
    return 3;
  };

  const openCollection = (collectionId: string) =>
    router.push(`/${routes.curatedCollections.root}/${collectionId}`);

  const onClickFollow = (collection: CollectionProperties) => {
    const isFollowed = collection.isFollowing;
    if (isFollowed) {
      dispatch(unfollowCollection(collection));
      showNotification({
        message: t('You have successfully unsubscribed from the collection {{name}}.', { name: collection.title }),
        severity: 'success',
      });
    } else {
      dispatch(followCollection(collection));
      showNotification({
        message: t('You have successfully subscribed to the collection {{name}}.', { name: collection.title }),
        severity: 'success',
      });
    }
  };

  const getMenuItems = (collection: CollectionProperties): MenuItemProps[] => [
    {
      label: collection.isFollowing ? t('I follow') : t('Follow'),
      iconName: collection.isFollowing ? 'check' : 'add',
      onClick: () => onClickFollow(collection),
      color: palette.text.primary,
    },
  ];

  const getBanner = (collection: CollectionProperties) => {
    if (!collection.isPublic) return undefined;

    const subject = collection.subjects?.[0];
    const SubjectBanner = SubjectBanners[subject] ?? SubjectBanners.Didaktik;

    return <SubjectBanner.small className='subject-banner' />;
  };

  return (
    <Box data-testid='curated-collections-list-content'>
      <Typography
        sx={{ ...(typography.h6), paddingX: cardPadding, paddingY: spaces.md }}
      >
        {t('Curated collections from meinUnterricht')}
      </Typography>
      <Box
        data-testid='curated-collections-list'
        sx={{
          display: 'grid',
          padding: cardPadding,
          rowGap: spaces.md,
          columnGap: spaces.md,
          flexShrink: 0,
          alignSelf: 'stretch',
          gridTemplateColumns: `repeat(${getGridTemplateColumns()}, minmax(0, 1fr))`,
          gridAutoFlow: 'row',
        }}
      >
        {collections.map((collection) => (
          <CustomMuiCard
            key={collection.id}
            title={collection.title}
            subheader={`${collection.contentsCount} ${collection.contentsCount === 1 ? t('Content') : t('Contents')}`}
            onClick={() => openCollection(collection.id)}
            menuItems={getMenuItems(collection)}
            color={collection.color}
            banner={getBanner(collection)}
            sxCardStyle={{
              main: {
                cursor: 'pointer',
                '&:focus-visible': {
                  outline: 'none',
                  borderColor: palette.primary.focusVisible,
                  boxShadow: boxShadows.elevation2,
                  '& .MuiCardHeader-title': {
                    color: palette.primary.main,
                  },
                },
                '&:hover': {
                  boxShadow: boxShadows.elevation2,
                  '& .MuiCardHeader-title': {
                    color: palette.primary.main,
                  },
                },
              },
              banner: {
                height: '180px',
                width: 'auto',
                maxWidth: '100%',
                '& .subject-banner': { height: '100%', width: '100%' },
              },
              subheader: {
                ...(typography.body2),
              },
            }}
          >
            <CollectionContent collection={collection}/>
          </CustomMuiCard>
        ))}
      </Box>
    </Box>
  );
};

export default CuratedCollectionsList;
